// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, CreditCard, Calendar } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PaymentPlanOption } from '@/types/paymentPlanTypes';

interface PaymentPlanSelectorProps {
  membershipId: string;
  membershipName: string;
  originalPrice: number;
  onPlanSelect: (planId: string | null, priceCents: number, billingType: string, paymentPlanId?: string) => void;
  selectedPlanId: string | null;
}

interface MembershipPaymentPlan {
  id: string;
  full_price_cents: number;
  plan_price_cents: number;
  installment_amounts: number[];
  template: {
    name: string;
    installment_count: number;
  };
}

export const PaymentPlanSelector: React.FC<PaymentPlanSelectorProps> = ({
  membershipId,
  membershipName,
  originalPrice,
  onPlanSelect,
  selectedPlanId
}) => {
  const [paymentPlans, setPaymentPlans] = useState<MembershipPaymentPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPaymentPlans = async () => {
      try {
        const { data, error } = await supabase
          .from('membership_payment_plans')
          .select(`
            id,
            full_price_cents,
            plan_price_cents,
            installment_amounts,
            payment_plan_templates!inner(
              name,
              installment_count
            )
          `)
          .eq('membership_id', membershipId)
          .eq('is_active', true);

        if (error) {
          console.error('Error loading payment plans:', error);
          return;
        }

        const formattedPlans = data?.map((plan: any) => ({
          id: plan.id,
          full_price_cents: plan.full_price_cents,
          plan_price_cents: plan.plan_price_cents,
          installment_amounts: plan.installment_amounts,
          template: {
            name: plan.payment_plan_templates.name,
            installment_count: plan.payment_plan_templates.installment_count
          }
        })) || [];

        setPaymentPlans(formattedPlans);
      } catch (error) {
        console.error('Exception loading payment plans:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPaymentPlans();
  }, [membershipId]);

  const formatPrice = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getSavings = (originalPrice: number, planPrice: number): number => {
    return originalPrice - planPrice;
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded mb-2"></div>
        <div className="h-20 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (paymentPlans.length === 0) {
    return null;
  }

  // Check if there's already a single payment discount option
  const hasSinglePaymentDiscount = paymentPlans.some(plan => plan.template.installment_count === 1);

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Options</h4>
      
      {/* Full Price Option - only show if no single payment discount exists */}
      {!hasSinglePaymentDiscount && (
        <Card 
          className={`cursor-pointer transition-all ${
            selectedPlanId === null ? 'border-2 border-blue-500 bg-blue-50' : 'border border-gray-200 hover:border-gray-300'
          }`}
            onClick={() => onPlanSelect(null, originalPrice, 'one_time')}
        >
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  selectedPlanId === null ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`}>
                  {selectedPlanId === null && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold text-gray-900">Pay in Full</span>
                  </div>
                  <div className="text-sm text-gray-600">One-time payment</div>
                </div>
              </div>
              <div className="text-right ml-4">
                <div className="font-bold text-lg text-gray-900">{formatPrice(originalPrice)}</div>
                <div className="text-xs text-gray-500 mt-1">Total</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Plan Options */}
      {paymentPlans.map((plan) => {
        const savings = getSavings(originalPrice, plan.plan_price_cents);
        const isSelected = selectedPlanId === plan.id;
        const isMultiPayment = plan.template.installment_count > 1;
        
        return (
          <Card 
            key={plan.id}
            className={`cursor-pointer transition-all ${
              isSelected ? 'border-2 border-green-500 bg-green-50' : 'border border-gray-200 hover:border-gray-300'
            } ${isMultiPayment ? 'min-w-0' : ''}`}
            onClick={() => onPlanSelect(plan.id, plan.installment_amounts[0], 'payment_plan', plan.id)}
          >
            <CardContent className="p-5">
              {isMultiPayment ? (
                // Multi-payment layout - stack vertically for better readability
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-green-500 bg-green-500' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold text-gray-900">{plan.template.name}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {plan.template.installment_count} payments of {formatPrice(plan.installment_amounts[0])}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pl-9">
                    <div>
                      <div className="text-lg font-bold text-gray-900">{formatPrice(plan.installment_amounts[0])}</div>
                      <div className="text-xs text-gray-500">First payment</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Total: {formatPrice(plan.plan_price_cents)}</div>
                      {savings > 0 && (
                        <div className="text-xs text-green-600 font-medium">
                          Save {formatPrice(savings)}!
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // Single payment layout - horizontal
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                      isSelected ? 'border-green-500 bg-green-500' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold text-gray-900">{plan.template.name}</span>
                        {savings > 0 && (
                          <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs px-2 py-0.5">
                            Save {formatPrice(savings)}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">Single payment</div>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="font-bold text-lg text-gray-900">{formatPrice(plan.installment_amounts[0])}</div>
                    <div className="text-xs text-gray-500 mt-1">Total</div>
                    {savings > 0 && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        Save {formatPrice(savings)}!
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};